import React, { useEffect, useState } from 'react';
import { FaPhone, FaPhoneSlash, FaTimes, FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa';

const TERMINAL_STATUSES = new Set(['ended', 'disconnected', 'failed', 'rejected']);

const formatDuration = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return `${mm} min, ${ss} sec`;
};

const STATUS_LABEL = {
  ringing: 'Incoming call…',
  connecting: 'Connecting…',
  connected: 'Connected',
  disconnecting: 'Ending…',
  ended: 'Call ended',
  rejected: 'Rejected',
  disconnected: 'Disconnected',
  failed: 'Call failed'
};

const STATUS_COLOR = {
  ringing: '#f59e0b',
  connecting: '#6b7280',
  connected: '#16a34a',
  disconnecting: '#6b7280',
  ended: '#6b7280',
  rejected: '#6b7280',
  disconnected: '#6b7280',
  failed: '#dc2626'
};

function IncomingCallPill({
  fromNumber,
  studentName,
  status,
  muted,
  onAccept,
  onReject,
  onToggleMute,
  onEndCall,
  onClose
}) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    // Reset the timer whenever a fresh call cycle begins. Without this the
    // pill — which can outlive a single call if the agent hasn't clicked the
    // close button when the next call comes in — would continue from the
    // previous call's elapsed value once the new one connects.
    if (status === 'ringing' || status === 'connecting') {
      setElapsed(0);
    }
    if (status !== 'connected') return undefined;
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [status]);

  const statusLabel = STATUS_LABEL[status] || status;
  const statusColor = STATUS_COLOR[status] || '#6b7280';

  return (
    <div
      style={{
        position: 'fixed',
        top: '90px',
        right: '20px',
        zIndex: 9999,
        background: '#ffffff',
        borderRadius: '9999px',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
        padding: '8px 8px 8px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        border: '1px solid #e5e7eb',
        minWidth: '340px'
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, gap: '6px' }}>
        <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
          {studentName || fromNumber || 'Unknown caller'}
        </span>
        {studentName && fromNumber && (
          <span style={{ fontSize: '11px', color: '#6b7280' }}>{fromNumber}</span>
        )}
        <span
          style={{
            fontSize: '12px',
            color: statusColor,
            display: 'flex',
            gap: '6px',
            alignItems: 'center'
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: '6px',
              height: '6px',
              borderRadius: '9999px',
              background: statusColor
            }}
          />
          {statusLabel}
          {status === 'connected' && (
            <span style={{ color: '#6b7280', marginLeft: '4px' }}>· {formatDuration(elapsed)}</span>
          )}
        </span>
      </div>

      {status === 'ringing' && (
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={onReject}
            aria-label="Reject"
            title="Reject"
            style={{
              background: '#ef4444',
              color: '#ffffff',
              border: 'none',
              borderRadius: '9999px',
              padding: '8px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500
            }}
          >
            <FaPhoneSlash size={14} style={{ transform: 'rotate(90deg)' }} />
            Reject
          </button>
          <button
            type="button"
            onClick={onAccept}
            aria-label="Accept"
            title="Accept"
            style={{
              background: '#16a34a',
              color: '#ffffff',
              border: 'none',
              borderRadius: '9999px',
              padding: '8px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500
            }}
          >
            <FaPhone size={14} style={{ transform: 'rotate(90deg)' }} />
            Accept
          </button>
        </div>
      )}

      {status === 'connected' && (
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={onToggleMute}
            aria-label={muted ? 'Unmute' : 'Mute'}
            title={muted ? 'Unmute' : 'Mute'}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '9999px',
              background: muted ? '#fee2e2' : '#f3f4f6',
              color: muted ? '#b91c1c' : '#374151',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
              flexShrink: 0
            }}
          >
            {muted ? <FaMicrophoneSlash size={16} /> : <FaMicrophone size={16} />}
          </button>
          <button
            type="button"
            onClick={onEndCall}
            style={{
              background: '#ef4444',
              color: '#ffffff',
              border: 'none',
              borderRadius: '9999px',
              padding: '8px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500
            }}
          >
            <FaPhoneSlash size={14} style={{ transform: 'rotate(90deg)' }} />
            End call
          </button>
        </div>
      )}

      {TERMINAL_STATUSES.has(status) && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            marginLeft: 'auto',
            background: '#111827',
            color: '#ffffff',
            border: 'none',
            borderRadius: '9999px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
            flexShrink: 0
          }}
        >
          <FaTimes size={16} color="#ffffff" />
        </button>
      )}
    </div>
  );
}

export default IncomingCallPill;
